import {
  DropdownMenuBodyWrapper,
  DropdownSize,
  dropdownMenuWrapperCSS,
} from '@ardoq/dropdown-menu-ui';
import { useEffectOnce, useIsMounted } from '@ardoq/hooks';
import { timeFastMs } from '@ardoq/design-tokens';
import { RefObject, ComponentType, useRef, useState } from 'react';
import { MenuListProps, components } from 'react-select';
import styled, { createGlobalStyle } from 'styled-components';
import { CommonSelectProps, SelectOption } from '../../types';

// https://github.com/styled-components/styled-components/issues/2113#issuecomment-730411620
const MenuListStyle = styled.div``;

const GlobalMenuListStyle = createGlobalStyle<{
  $dropdownSize: DropdownSize;
  $maxDropdownHeight?: number;
}>`
  ${MenuListStyle} {
    && {
      ${dropdownMenuWrapperCSS};
      padding: 0;
      display: flex;
      flex-direction:column;
      overflow: hidden;

      ${DropdownMenuBodyWrapper} {
        overflow: auto;
      }

      /*
      * ANIMATION
      */
      transition: opacity ${timeFastMs} ease-in-out, transform ${timeFastMs} ease-in-out;
      opacity: 0;
      transform: translateY(-20px);
      &.is-visible {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

/**
 * What's that?
 *
 * FIX for ARD-14876 "Dropdown menu in filter is cut  and stuck at the top"
 *
 * This is a hacky way to cover a case when opened menu overflows the view port.
 * This can happen in each of the following cases:
 * 1) Viewport height is smaller than the list itself (by default below 300px)
 * 2) Input is somewhere in the middle of the small screen and there is no enough
 * spaces below nor above the input
 *
 * In worst case scenario we are speaking here about viewport smaller than 450px,
 *
 * How do we fix the issue?
 * Unfortunately react-select doesn't provide us convenient interface to play with
 * menu position utils. That's why for now I am accessing the DOM directly
 *
 */
const useVerticalOverflowFix = (
  bodyWrapperRef: RefObject<HTMLDivElement | null>,
  skipFix?: boolean
) => {
  const [maxWrapperHeight, setMaxWrapperHeight] = useState<
    number | undefined
  >();
  useEffectOnce(() => {
    if (skipFix) return;
    setTimeout(() => {
      if (!bodyWrapperRef.current) return;
      const bodyWrapper = bodyWrapperRef.current;
      const wrapperRect = bodyWrapper.getBoundingClientRect();
      const viewportHeight = Math.max(
        document.documentElement.clientHeight || 0,
        window.innerHeight || 0
      );

      const listIsOverflowingFromBottom = wrapperRect.bottom > viewportHeight;
      const listIsOverflowingFromTop = wrapperRect.top < 0;
      if (!listIsOverflowingFromBottom && !listIsOverflowingFromTop) return;

      const menuListEl = bodyWrapper.parentElement?.parentElement;
      if (!menuListEl) return;
      const { x: left, width } = menuListEl.getBoundingClientRect();
      menuListEl.style.transitionDuration = '0';
      menuListEl.style.position = 'fixed';
      menuListEl.style.left = `${left}px`;
      menuListEl.style.top = '0';
      menuListEl.style.width = `${width}px`;
      setMaxWrapperHeight(viewportHeight - 32);
    }, 0);
  });
  return { maxWrapperHeight };
};

export const MenuList: ComponentType<
  MenuListProps<SelectOption<unknown>>
> = props => {
  const bodyWrapperRef = useRef<HTMLDivElement | null>(null);
  const { maxWrapperHeight } = useVerticalOverflowFix(
    bodyWrapperRef,
    props.selectProps.menuPortalTarget === null
  );
  const isMounted = useIsMounted();
  const { footerContent } = props.selectProps as unknown as Pick<
    CommonSelectProps,
    'footerContent'
  >;

  const maxDropdownHeight = props.maxHeight
    ? props.maxHeight
    : maxWrapperHeight; // maxWrapperHeight will be smaller if the menu list is too high for the screen

  return (
    <>
      <GlobalMenuListStyle
        $dropdownSize={DropdownSize.FULL_WIDTH}
        $maxDropdownHeight={maxDropdownHeight}
      />
      <components.MenuList
        {...props}
        className={[
          MenuListStyle.styledComponentId,
          (isMounted && 'is-visible') || '',
        ].join(' ')}
      >
        <DropdownMenuBodyWrapper
          ref={bodyWrapperRef}
          style={{ maxHeight: maxWrapperHeight }}
        >
          {props.children}
        </DropdownMenuBodyWrapper>
        {footerContent}
      </components.MenuList>
    </>
  );
};
